<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="argueBelief ? $t('scenario.argueBelief') : $t('scenario.addMinimalScenarii')"
    />

    <div class="px-12 pt-6 pb-2" v-if="!loading">
      <div class="d-inline-block mr-3">
        <h4 class="text-uppercase" style="letter-spacing: -1px;">
          {{ argueBelief ? $t('scenario.belief') : $t('option.selectOption') }}:
        </h4>
      </div>
      <div class="d-inline-block mr-3">
        <v-select
            v-if="!loadingOptions"
            v-model="selectedOption"
            @change="selectedOptionChanged"
            :items="options"
            :item-text="showLogicalLanguage ? 'prolog' : 'name'"
            item-value="id"
            return-object
            :label="$t('global.select')"
            solo
        >
          <template v-slot:item="data">
            {{
              showLogicalLanguage ? data.item.prolog || $t('global.notranslation', {something: data.item.name}) : data.item.name || $t('global.notranslation', {something: data.item.prolog})
            }}
          </template>
        </v-select>
        
        <v-progress-circular
          v-else
          size="30"
          width="5"
          class="d-block mx-auto"
          indeterminate
          color="primary"
        />
      </div>
    </div>
    
    <v-progress-circular
      v-else
      size="200"
      width="15"
      class="d-block mx-auto"
      indeterminate
      color="primary"
    />

    <v-container
      v-if="selectedOption"
      fluid
      class="px-12 pt-0 mb-12 pb-12"
    >
      <!-- selected option -->
      <option-block
        v-if="!argueBelief"
        :list-label="$t('global.option') + ' ' + selectedOption.id"
        :options="[selectedOption]"
      />
        <!-- option scenarii -->
        <scenario-block
          v-for="pref in preferencesOfOption"
          :key="pref.id"
          :scenario="pref.scenario"
          delete-scenario
          :allow-set-up="isScenarioEditable(pref.scenario)"
          :delete-scenario-function="deleteScenario"
          :delete-element="isScenarioEditable(pref.scenario)"
          :delete-element-function="isScenarioEditable(pref.scenario) ? deleteElement : () => null"
          :allow-scenario-edition="isScenarioEditable(pref.scenario)"
          :available-elements="elements"
          :update-available-elements="updateElementList"
          :update-available-scenarios="updateScenariosList"
          :enableResolvedCheckMark=false
          v-on="$listeners"
          :preference="pref"
        />
        <!-- Add elements to scenario -->
        <add-elements-to-scenarii
          :preferences-of-option="preferencesOfOption"
          :calling-api="callingApi"
          :validate-scenarii="validateScenarii"
          :elements="elements"
          :add-element-list="addElementList"
          :update-element-list="updateElementList"
          v-on="$listeners"
          :argue-belief="argueBelief"
        />
        <v-row
          no-gutters
          justify="center"
        >
          <v-col cols="1" class="mx-2">
            <raison-button
              :button-label="$t('option.nextOption')"
              :button-function="goToNextOption"
              :loading="callingApi"
              :disabled="noMoreNextOption"
            />
          </v-col>
        </v-row>

    </v-container>
    <footer-navigation
      v-if="argueBelief"
      :prev-button-function="backToScenariiList"
      prev-button-label="project.knowledgeRepresentation"
      :next-button-function="goToResolveConflictWithMinimalScenariosForBeliefs"
      next-button-label="project.resolveConflictsInCombinationsOfMinimumScenariosForBeliefs"
    />
    <footer-navigation
      v-if="!argueBelief"
      :prev-button-function="backToIdentifyOptions"
      prev-button-label="option.identify"
      :next-button-function="goToAddMoreOptionToAMinimalScenarii"
      next-button-label="scenario.addMoreOptionsToAMinimalScenario"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import ScenarioBlock from '@/components/global/ScenarioBlock';
import AddElementsToScenarii from '@/components/addScenarii/AddElementsToScenarii';
import FooterNavigation from '@/components/layout/FooterNavigation';
import OptionBlock from '@/components/global/OptionBlock';
import RaisonButton from '@/components/global/RaisonButton';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import optionService from '@/services/options.js';
import scenarioService from '@/services/scenarios.js';
import elementService from '@/services/elements.js';

export default {
  name: 'AddScenarii',

  components: {PageContentToolbar, ScenarioBlock, AddElementsToScenarii, FooterNavigation, OptionBlock, RaisonButton},

  mixins: [projectPageMixin],

  data() {
    return {
      projectId: '',
      beliefId: '',
      preferences: [],
      options: [],
      allElements: [],
      selectedOption: undefined,
      loading: true,

      loadingElements: false,
      loadingOptions: false,
      loadingScenarios: false,
      callingApi: false,
      noMoreScenariiForOption: false,
      argueBelief: false,
      elts: [],
    };
  },

  computed: {
    noMoreNextOption () {
      return this.currentOptionIndex >= this.options.length - 1
    },

    currentOptionIndex() {
      const isCurrentOption = (opt) => opt.id === this.selectedOption.id
      return this.options.findIndex(isCurrentOption)
    },

    showLogicalLanguage() {
      return this.$store.state.showLogicalLangage;
    },

    preferencesOfOption() {
      return this.preferences.filter(pref => {
        if(!scenarioService.isMinimalScenario(pref.scenario) || scenarioService.isDiscriminatePreference(pref)) {
          return false;
        }
        let wigos;
        if(this.beliefId) {
          wigos = pref.wigoBelief;
        }
        else {
          wigos = pref.wigo;
        }
        return wigos.findIndex(wigo => wigo.id === this.selectedOption.id) >= 0;
      });
    },

    elements() {
      return this.allElements.filter(elt => elt.id !== this.selectedOption.id);
    },

  },

  methods: {

    goToNextOption() {
      this.selectedOption = this.options[this.currentOptionIndex + 1]
    },

    selectedOptionChanged() {
      this.noMoreScenariiForOption = false;
    },

    validateScenarii(elements) {
      this.callingApi = true;
      let newScenario = {
        name: 'minimal scenario',
        level: '1',
        elements: [...elements],
      };

      // create scenario
      let newPreference = {
        name: 'initial preference',
        scenario: newScenario,
      };
      if (this.argueBelief) {
        newPreference.wigoBelief = [{'id': this.selectedOption.id}];
      } else {
        newPreference.wigo = [{'id': this.selectedOption.id}];
      }

      scenarioService.createPreferences(this.projectId, [newPreference])
        .then(() => {
          this.$emit('raison-event', { type: 'success', messages: [this.$t('scenario.created')] });
          this.loadPreferences();
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        })
        .finally(() => {
          this.callingApi = false;
        });
    },

    backToScenariiList () {
      this.$router.push({name: 'scenariiList', params: { id: this.$route.params.id } });
    },

    backToIdentifyOptions() {
      this.$router.push({name: 'identifyOptions', params: {id: this.$route.params.id}});
    },

    goToAddMoreOptionToAMinimalScenarii() {
      this.$router.push({name: 'addOptionsToScenario', params: {id: this.$route.params.id, scenarioId: 'noId'}});
    },

    goToResolveConflictWithMinimalScenariosForBeliefs() {
      this.$router.push({name: 'conflictResolutionInCombinationForBeliefs', params: { id: this.$route.params.id, beliefId: this.selectedOption.id } });
    },

    loadPreferences() {
      this.callingApi = true;
      scenarioService.getAllPreferences(this.projectId)
        .then(response => {
          this.preferences.splice(0, this.preferences.length, ...response.data);
        })
        .catch(error => {
          this.$emit('raison-event', { type: 'error', messages: [error] });
        })
        .finally(()=> {
          this.callingApi = false;
        });
    },

    /**
     * Method permit to delete an element in given scenario from an event emited by ScenarioBlock component
     */
    deleteElement(scenario, element) {
      let copyScenario = JSON.parse(JSON.stringify(scenario));

      this.removeElementOfScenario(copyScenario, element);

      scenarioService.updateScenario(this.$route.params.id, copyScenario).then(() => {
        this.loadPreferences();
      })
      .catch(error => {
        this.$emit('raison-event', { type: 'error', messages: [error]} );
      });
    },

    /**
     * Method permit to delete a scenario from an event emited by ScenarioBlock component
     */
    deleteScenario(preferenceId) {
      scenarioService.deleteScenarioPreference(this.$route.params.id, preferenceId, true).then(() => {
        this.preferences.splice(this.preferences.findIndex(prefItem => prefItem.id === preferenceId), 1);
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      })
    },

    removeElementOfScenario(scenario, element) {
      scenario.elements.splice(scenario.elements.findIndex(elementItem => elementItem.id === element.id), 1);
    },

    addElementList(element) {
      this.elements.push(element);
    },

    updateElementList(elements) {
      // by convention the newly updated elements is the first
      this.allElements.splice(0, this.allElements.length, ...elements);
      
      // update all other references of the update element (the first by convention)
      this.preferencesOfOption.forEach(pref => {
        if (pref.scenario.elements) {
          pref.scenario.elements.forEach((elementItem, index, object) => {
            if (elementItem.id === elements[0].id) {
              object.splice(index, 1, elements[0]);
            }
          });
        }
      });
    },

    replaceElementOnArray(arrayElements, elementToReplace) {
      arrayElements.splice(arrayElements.findIndex(elem => elem.id === elementToReplace.id), 1, elementToReplace);
    },

    updateScenariosList() {
      this.loadPreferences();
    },

    emitEvent(eventType, eventName, eventContent) {
      this.$emit(eventType, eventName, eventContent);
    },

    isScenarioEditable (scenario) {
      return scenario.nextLevel.length === 0 && scenario.extend === "true"
    }

  },

  created() {
    this.projectId = this.$route.params.id;
    this.beliefId = this.$route.params.beliefId;
    this.argueBelief = !!this.beliefId;
    this.loading = true;
    Promise.all([
      scenarioService.getAllPreferences(this.projectId),
      optionService.getOptions(this.projectId),
      elementService.getElements(this.projectId)
    ])
    .then(responses => {
      this.preferences = responses[0].data;
      this.options = responses[1].data;
      this.allElements = responses[2].data;

      if(this.beliefId) {
        this.options = this.allElements.filter(elt => elt.type === 'belief');
        this.selectedOption = this.allElements.find(elt => elt.id === this.beliefId);
      }
      else {
        this.selectedOption = this.options[0];
      }
    })
    .finally(() => {
      this.loading = false;
    });

  },
};
</script>

<style>
.rai-input-element .v-input__control {
  height: 55px;
  margin-top: -1px;
}

.rai-add-element__button {
  width: 100%;
  border: 2px grey dashed;
  height: 56px;
}
</style>
